/**
 * LOGISPRD-12506 — 온콜 콘솔 API DTO.
 *
 * 핸드오프 §4 를 그대로 옮긴 계약이다. 1단계에서는 목데이터가 이 타입을 만족하도록 강제하는
 * 역할만 하고, 2단계(API 배선)에서 실제 응답 타입으로 그대로 쓰인다. 필드명·옵셔널 여부를
 * 임의로 바꾸지 마라 — 백엔드 Kotlin DTO 와의 계약이다.
 */

/** 핸드오프 §4.1 — Hold (규약 B: 성공은 raw DTO, 실패만 ApiResponseV2) */
export type HoldDomain = 'fulfillment-dispatcher' | '29cm-dispatcher' | 'fulfillment' | '*'

export type HoldPredicate =
  | { op: 'leaf'; field: string; values: string[]; negate?: boolean }
  | { op: 'and'; nodes: HoldPredicate[] }
  | { op: 'or'; nodes: HoldPredicate[] }

export type MaintenanceHoldRequest = { holdId: string; domain: string; from: number; toExclusive: number; reason?: string }
export type AttributeHoldRequest = {
  holdId: string
  domain: string
  field: string
  values: string[]
  from?: number
  until?: number | null
  reason?: string
}
export type PredicateHoldRequest = {
  holdId: string
  domain: string
  predicate: HoldPredicate
  from?: number
  until?: number | null
  reason?: string
}

export type HoldRuleView = {
  type: 'maintenance' | 'attr' | 'predicate'
  holdId: string
  domain: string
  from?: number
  until?: number
  toExclusive?: number
  field?: string
  values?: string[]
  predicate?: HoldPredicate
  reason?: string
}

export type HeldWorkflowView = {
  workflowId: string
  workflowType: string
  orderId?: string
  orderItemId?: string
  waitType?: string
  waitReason?: string
  waitStartedAt?: string
}
export type HeldPage = { items: HeldWorkflowView[]; nextPageToken?: string }
export type RecheckResult = { jobId: string; visibilityQuery: string }

/** §4.2 — 재발사 (규약 B) */
export type ManualRedispatchRequest = { standardOrderIds: number[]; wakeUp?: boolean }
export type ManualRedispatchResult = {
  standardOrderId: number
  accepted: boolean
  dedup: boolean
  routedToOptimizer: boolean
  wokeUp: boolean
  failureReason?: string
}
export type ManualRedispatchResponse = { results: ManualRedispatchResult[] }

/*
 * 범위 축소(2026-09-15, 사용자 결정) — §4.4 마스터 상품 타입(`MasterProductRowResponse`·
 * `OptionPriority*`·`RfidBarcodeMapping*`·`UpdateMasterProductRequest`·`MasterProductResponse`)을
 * 뺐다. 바코드 보정·재고 대조 섹션을 제거하면서 참조가 없어졌다. 근거는 `shell/tabs.ts` 주석 참고.
 */

/*
 * 범위 축소(2026-09-15) — §4.5 합포 재개(`ShipmentRetry*`) 타입을 뺐다. `hasAuthority('MASTER')` 라
 * 이 콘솔의 대상(`MOMS_ENG`)으로는 항상 401 이다. 근거는 `shell/tabs.ts` 상단 주석 참고.
 */

/**
 * §4.6 — 정산 재발행 (규약 B, `MOMS_ENG`). LOGISPRD-13362 — 11517(moms-be PR #3988)이 인가를
 * `MASTER` → `MOMS_ENG` 로 바꿔 되살렸다. BE `ReturnInboundSettlementAdminRequests.kt` 와 같은 모양.
 */
export type RepublishRequest = { inboundReportIds: number[]; reason: string }
export type RepublishOutcome = 'PUBLISHED' | 'SKIPPED' | 'NOT_FOUND' | 'FAILED'
export type RepublishResponse = {
  requested: number
  duplicatesIgnored: number
  published: number
  skipped: number
  notFound: number
  failed: number
  operatorIdentity: string
  /** `detail` 은 Kotlin `String?` — PUBLISHED 면 `null` 로 온다. */
  items: { inboundReportId: number; outcome: RepublishOutcome; detail?: string | null }[]
}

/*
 * 범위 축소(2026-09-15, 사용자 결정) — §4.8 캐파 기본값(`CapacityType`·`CapacityDefaultResponse`·
 * `WarehouseCapacityDefaultRequest`·`WarehouseCapacityDefaultDeleteRequest`)과 §4.9 Kafka 컨슈머
 * 시간창 정지(`KafkaConsumerWindow`·`KafkaConsumerDynamicConfig`) 타입을 뺐다. `설정` 탭을 통째로
 * 제거하면서 참조가 없어졌다. 근거는 `shell/tabs.ts` 상단 주석 참고.
 */

/*
 * LOGISPRD-13034 · 13035 — 개발자 콘솔 주문 조회 · Trace (moms-be PR #3818).
 * BE DTO(`DevConsoleOrderDtos.kt` · `DevConsoleOrderTraceDtos.kt`)를 그대로 옮겼다. Kotlin nullable 은
 * Jackson 이 `null` 로 내보내므로 `| null` 로 둔다.
 */
export type OrderStatusCode =
  | 'HOLD'
  | 'ORDER_COLLECTED'
  | 'ORDER_ACCEPTED'
  | 'INV_UNALLOCATED'
  | 'INV_ALLOCATED'
  | 'SPLIT_COMPLETED'
  | 'REPLANNED'
  | 'CANCELED'
  | 'CLOSED'
  | 'OUTBOUND_REQUESTED'
  | 'PARTIAL_OUTBOUND'
  | 'OUTBOUND_COMPLETED'

export type DevConsoleOrderRow = {
  standardOrderId: number
  shipperCode: string | null
  orderNumber: string | null
  orderOptionNumber: string | null
  orderStatus: OrderStatusCode | null
  orderType: string | null
  outboundCutSeqId: number | null
  orderConsolNumber: string | null
  orderQuantity: number | null
  collectedDate: string | null
  plannedCutOffDate: string | null
  refNo1: string | null
  refNo2: string | null
  warehouseName: string | null
  cutoffMode: string | null
  parentOrderNumber: string | null
  parentOrderOptionNumber: string | null
  skuId: string | null
  barcode: string | null
  optionCode: number | null
  goodsCode: string | null
  goodsName: string | null
  goodsOption: string | null
  brandName: string | null
  /** 서버 ObjectMapper 가 NON_NULL 이라 값이 없으면 키째 빠진다. BE 는 후속 티켓에서 이 이름으로 추가한다. */
  addressId?: number | null
}

export type DevConsoleOrderPage = { items: DevConsoleOrderRow[]; hasNext: boolean; nextLastId: number | null }

export type OrderSearchParams = {
  limit: number
  lastId?: number
  outboundCutSeqId?: number
  orderNumber?: string
  refNo1?: string
  orderOptionNumber?: string
  orderStatuses?: OrderStatusCode[]
  collectedDateFrom?: string
  collectedDateTo?: string
}

/** Trace 조회 키 — F-OPT 조회 행에서 Trace 탭으로 넘길 때도 이 모양을 쓴다. */
export type OrderTraceKey = { refNo1: string; orderOptionNumber: string }

export type TraceSection = {
  key: string
  table: string
  /** 구간당 200행을 넘어 잘렸는지(인계서 개정본). */
  truncated: boolean
  rows: Record<string, unknown>[]
  /** 실패 시 고정 코드 `SECTION_QUERY_FAILED` 뿐 — 원인은 서버 로그에만 있다. */
  error?: string | null
  skippedReason?: string | null
}

export type BizestVerdictStatus = 'SENT' | 'RESEND_PENDING' | 'CANCEL_SKIPPED' | 'NOT_SENT'
export type BizestOutboundResultVerdict = { id: number | null; status: BizestVerdictStatus }

export type DevConsoleOrderTrace = {
  refNo1: string
  orderOptionNumber: string
  orders: DevConsoleOrderRow[]
  /** 주문이 50건을 넘어 잘렸는지. true 면 잘린 주문의 구간 행도 빠져 있다. */
  ordersTruncated: boolean
  bizestOutboundResults: BizestOutboundResultVerdict[]
  sections: TraceSection[]
}

/*
 * SKU 조회 · 주문 상품 sku_id 백필 (docs/DEV_CONSOLE_SKU_LOOKUP_BACKFILL_API.md §4).
 * 서버 ObjectMapper 가 NON_NULL 이라 null 필드는 키 자체가 빠진다 — 옵셔널과 nullable 을 함께 선언한다.
 */
export const SKU_LOOKUP_PLATFORMS = ['MUSINSA', 'CM29'] as const
export type SkuLookupPlatform = (typeof SKU_LOOKUP_PLATFORMS)[number]

export type SkuLookupOutcome = 'FOUND' | 'UNMAPPED' | 'LOOKUP_FAILED' | 'UNKNOWN_SHIPPER' | 'UNSUPPORTED_SERVICE_TYPE'

export type ProductOptionSkuLookupItem = { platform: SkuLookupPlatform; uid: number; optionCode: number }
export type ProductOptionSkuLookupRequest = { items: ProductOptionSkuLookupItem[] }
export type ProductOptionSkuLookupResult = ProductOptionSkuLookupItem & {
  outcome: SkuLookupOutcome
  skuId?: string | null
  /** SKU 운영상태가 아니라 매핑의 유효 여부다 — 백필 판정에 쓰지 않는다. */
  active?: boolean | null
}
export type ProductOptionSkuLookupResponse = { results: ProductOptionSkuLookupResult[] }

export type OwnerBarcodeSkuLookupItem = { shipperCode: string; barcode: string }
export type OwnerBarcodeSkuLookupRequest = { items: OwnerBarcodeSkuLookupItem[] }
export type OwnerBarcodeSkuLookupResult = OwnerBarcodeSkuLookupItem & {
  outcome: SkuLookupOutcome
  skuId?: string | null
  active?: boolean | null
}
export type OwnerBarcodeSkuLookupResponse = { results: OwnerBarcodeSkuLookupResult[] }

export type SkuBackfillOutcome =
  | 'UPDATED'
  | 'UNCHANGED'
  | 'SKIPPED_ALREADY_FILLED'
  | 'NOT_FOUND'
  | 'INVALID_SKU'
  | 'SKU_LOOKUP_FAILED'
  | 'CONFLICT'
  | 'WRITE_FAILED'

export type SkuBackfillItem = { standardOrderProductId: number; skuId: string }
export type SkuBackfillRequest = { items: SkuBackfillItem[]; overwrite: boolean }
export type SkuBackfillResult = {
  standardOrderProductId: number
  requestedSkuId: string
  /** 쓰기 전에 읽은 값. 잘못 덮어쓴 것을 되돌릴 유일한 근거다 — NULL 이었거나 행이 없으면 키가 빠진다. */
  previousSkuId?: string | null
  outcome: SkuBackfillOutcome
}
export type SkuBackfillResponse = { results: SkuBackfillResult[] }

/* 반품 입고실적 원주문 재매핑 (docs/ONCALL_RETURN_ORIGIN_REMAP_API.md §4). 거부도 200 이고 outcome 으로 가른다. */
export type ReturnOriginRemapRequest = {
  misMappedInboundReportId: number
  blockedInboundReportId: number
  correctRefNo1: string
  correctRefNo2: string
  reason: string
  /** 생략·null 이면 서버가 true 로 본다. 실행은 false 를 명시했을 때만 일어난다. */
  dryRun: boolean
}

export type ReturnOriginRemapOutcome = 'DRY_RUN_PASSED' | 'APPLIED' | 'REJECTED'

export type ReturnOriginRemapRejectionCode =
  | 'INVALID_MIS_MAPPED_REPORT'
  | 'INVALID_BLOCKED_REPORT'
  | 'NOT_SAME_COMBINED_SHIPMENT'
  | 'CLAIM_ID_CONVENTION_MISMATCH'
  | 'INBOUND_PLAN_NOT_UNIQUE'
  | 'SOURCE_ERP_ROW_MISMATCH'
  | 'TARGET_LINE_NOT_REMAPPABLE'
  | 'ERP_ROW_NOT_CREATED'

export type ReturnOriginRemapRejection = { code: ReturnOriginRemapRejectionCode; detail: string }

/** before/after 의 키가 테이블마다 다르다 — 입고실적·입고예정은 refNo1·refNo2·claimId, ERP 출처 이관은 srcTableId. */
export type ReturnOriginRemapChange = {
  table: 'UI_GRREPORT_DETAIL' | 'OPR_AN_STATUS' | 'OIF_LO_GI'
  id: number
  before: Record<string, string | number | null>
  after: Record<string, string | number | null>
}

export type ReturnOriginRemapCreatedErpRow = {
  id: number
  storeOrderNo: string
  storeOrderSeq: string
  sndSeq: string
  /** ISO 날짜 문자열 (예: '2026-09-16') */
  clsDate: string
  menge: number
  /** 생성 직후는 항상 '0'. 전송 배치 뒤 '1' 이 된다. */
  apiFlag: string
}

export type ReturnOriginRemapResponse = {
  outcome: ReturnOriginRemapOutcome
  rejection: ReturnOriginRemapRejection | null
  changes: ReturnOriginRemapChange[]
  createdErpRow: ReturnOriginRemapCreatedErpRow | null
  operator: string
  requestId: string
}

/* 글로벌 입고확정 롤백 · 재확정 (docs/GLOBAL_INBOUND_ROLLBACK_API.md §4). */
export type GlobalInboundRollbackOutcome = 'ROLLBACKABLE' | 'ROLLED_BACK' | 'ALREADY_ROLLED_BACK' | 'REJECTED' | 'FAILED'

export type GlobalInboundRollbackReason =
  | 'NOT_FOUND'
  | 'NOT_GLOBAL_CONFIRM'
  | 'ACTUAL_QTY_EXISTS'
  | 'RESULT_RECEIVED'
  | 'LES_DESTINATION'
  | 'NOT_RELEASED_YET'
  | 'POST_TASK_PENDING'
  | 'LEDGER_MISMATCH'
  | 'MWMS_CANCEL_FAILED'
  | 'MWMS_CANCELED_DB_FAILED'

export type MwmsCancelMode = 'REQUIRED' | 'ALREADY_CANCELED' | 'SKIPPED_BY_OPERATOR'

export type GlobalInboundRollbackRequest = {
  inboundDocumentNumbers: string[]
  mwmsAlreadyCanceledDocumentNumbers: string[]
  /** 생략·null 이면 서버가 dryRun 으로 본다. 실행은 false 를 명시했을 때만 일어난다. */
  dryRun: boolean
}

export type GlobalInboundRollbackResultItem = {
  inboundDocumentNumber: string
  cutDate: string | null
  cutSeq: number | null
  planCount: number | null
  ledgerRevertCount: number | null
  mwmsCancel: MwmsCancelMode | null
  outcome: GlobalInboundRollbackOutcome
  reason: GlobalInboundRollbackReason | null
  detail: string | null
}

/** 200 과 409(feature flag OFF)가 이 모양을 공유한다. */
export type GlobalInboundRollbackResponse = {
  dryRun: boolean
  results: GlobalInboundRollbackResultItem[]
  /** 409 에서만 채워진다. */
  message?: string
}

export type ConfirmGlobalInboundRequest = { shipperCode: string; warehouseCode: string }
/** 재확정만 규약 A(`ApiResponseV2`)다 — 생성된 입고지시 ID 목록. */
export type ConfirmGlobalInboundResponse = ApiResponseV2<{ items: number[] }>

/*
 * MCIS 컷오버 롤백 재송신(LOGISPRD-14487, moms-be PR #4568).
 * 정본은 moms-be `docs/lld/logistics/LLD-logistics-erp-result-mcis-rollback-resend.md` 와
 * `McisCutoverRollbackResendAdminRequests.kt` 다. 200 과 409 가 같은 raw DTO 를 쓴다.
 */
export type McisCutoverRollbackResendOutcome = 'DRY_RUN' | 'APPLIED' | 'NO_OP' | 'REJECTED_CUTOVER_ENABLED'

export type McisCutoverRollbackResendRowStatus = 'ELIGIBLE' | 'ALREADY_RELEASED' | 'NOT_MCIS_CUTOVER' | 'NOT_FOUND'

export type McisCutoverRollbackResendRequest = {
  /** `OIF_LO_GI.id`. 1~100건, 양수. */
  outboundResultErpIds: number[]
  reason: string
  /** 생략·null 이면 서버가 dryRun 으로 본다. 실행은 false 를 명시했을 때만 일어난다. */
  dryRun: boolean
}

/** `NOT_FOUND` 면 id 외 필드가 null 이다. */
export type McisCutoverRollbackResendRow = {
  id: number
  status: McisCutoverRollbackResendRowStatus
  clsDate: string | null
  storeOrderNo: string | null
  storeOrderSeq: string | null
  sndSeq: string | null
  claim: string | null
  /** "0" | "1" */
  apiFlag: string | null
  piStat: string | null
  ordRemark: string | null
}

export type McisCutoverRollbackResendResponse = {
  outcome: McisCutoverRollbackResendOutcome
  dryRun: boolean
  cutoverEnabled: boolean
  executeAllowed: boolean
  requestedCount: number
  eligibleCount: number
  /** 실제 갱신 건수의 정본. dryRun 이면 0. */
  releasedCount: number
  /** 실행 응답이면 갱신 전 분류 스냅샷이다. */
  rows: McisCutoverRollbackResendRow[]
  /** `admin:{로그인 ID}` */
  operatorIdentity: string
}

/**
 * 워크플로 restart(LOGISPRD-15715, moms-be PR #4886, `@devOpsGate.allowsOperation`). 서버 정본은
 * `WorkflowRestartController.kt` 의 `WorkflowRestartRequest` 와 `RestartWorkflowUsecase.Result` 다.
 * 성공 본문은 raw DTO(규약 B), 형식 오류·상한 초과는 400 `ApiResponseV2` 로 온다.
 */
export type WorkflowRestartTarget = {
  workflowId: string
  runId: string
  workflowType: string
}

/** 새로 시작은 `workflowIdPrefix`·`workflowType` 중 하나 이상, 이어받기는 `resumeTargets` 만 준다. */
export type WorkflowRestartRequest = {
  workflowIdPrefix?: string
  workflowType?: string
  resumeTargets?: WorkflowRestartTarget[]
}

export type WorkflowRestartFailure = {
  /** 그대로 `resumeTargets` 로 넘길 수 있다. */
  target: WorkflowRestartTarget
  /** 제출하지 못한 standard_order.id. 원본 run 을 읽기 전에 실패했으면 비어 있다. */
  itemIds: number[]
  reason: string
}

export type WorkflowRestartResponse = {
  totalCount: number
  cancelRequestedCount: number
  cancelledCount: number
  /** 건너뛴 사유(`UNSUPPORTED_TYPE`·`NOT_CANCELLABLE`·`NOT_REACHED`·`CANCEL_REQUEST_FAILED`·`RESUME_EXPIRED`) 또는 확인 시점 실행 상태별 수 */
  otherStatusCounts: Record<string, number>
  /** 이 호출이 새로 재제출을 시작한 주문 수. 호출 사이에 겹치지 않는다. */
  restartedOrderCount: number
  restartWorkflowIds: string[]
  pendingTargets: WorkflowRestartTarget[]
  restartFailures: WorkflowRestartFailure[]
  /** 앞 호출이 이미 시작해 이번에 건너뛴 청크. 주문 수 합계에 더하지 않는다. */
  alreadyStartedOrderCount: number
  alreadyStartedWorkflowIds: string[]
}

/**
 * LOGISPRD-15812 — 디폴트 센터 우선순위 일괄 반영. BE `SetDefaultPriorityGridRequest.kt`(LOGISPRD-12117)와 같은 모양.
 * 응답은 `StandardResponse<Unit>` 이라 `data` 는 비어 있고 `meta` 만 읽는다.
 */
export type DefaultPriorityType = 'WAREHOUSE_PRIORITY' | 'PACKING'
export type DefaultPriorityGridWarehouse = { warehouseCode: string; priority: number | null }
export type DefaultPriorityGridSlot = {
  startTime: string
  priorityType: DefaultPriorityType
  warehousePriorityEnabled: boolean
  warehouses: DefaultPriorityGridWarehouse[]
}
export type DefaultPriorityGridRequest = { shipperCode: string; cutType: string; slots: DefaultPriorityGridSlot[] }
export type DefaultPriorityGridResponse = ApiResponseV2<null>

/* ───────── LOGISPRD-15602 — 창고 정책 편집 (창고 탭) ─────────
 * 정본: moms-be WarehouseController · WarehouseTypedPolicyPayloads · Carrier enum.
 * swagger(warehouse.yml)는 코드와 어긋나 있어 코드를 따른다. */

/** BE `Carrier` enum 상수명 그대로 보낸다. 표시 순서가 곧 셀렉트 옵션 순서다. */
export const CARRIERS = ['JA', 'CJ', 'LXPANTOS', 'FLEXPORT', 'CJ_INEX', 'ETC', 'LT', 'HJ', 'TMALL', 'DELEO'] as const
export type Carrier = (typeof CARRIERS)[number]

export const CARRIER_LABELS: Record<Carrier, string> = {
  JA: '중앙일보',
  CJ: 'CJ대한통운',
  LXPANTOS: 'LX판토스',
  FLEXPORT: 'FLEXPORT',
  CJ_INEX: 'CJGLS',
  ETC: '용차/직접배송',
  LT: '롯데',
  HJ: '한진택배',
  TMALL: 'TMALL',
  DELEO: '델레오',
}

export type BooleanWarehousePolicyType = 'JA_PICKUP' | 'WCS_SORTER' | 'B2B_CUTOFF' | 'CAPACITY_MANAGED'
export type WarehousePolicyType = BooleanWarehousePolicyType | 'PARCEL_CARRIER' | 'OUTBOUND_SEQUENCE_PREFIX'

export type WarehousePolicy =
  /** `enabled` 키가 빠진 항목은 BE 읽기 로직이 false 로 본다. */
  | { type: BooleanWarehousePolicyType; enabled?: boolean | null }
  | { type: 'PARCEL_CARRIER'; carrier?: Carrier | null; shippers?: { code: string; carrier: Carrier }[] | null }
  | { type: 'OUTBOUND_SEQUENCE_PREFIX'; prefix: string }

export type WarehouseView = {
  warehouseCode: string
  warehouseName: string
  status: boolean
  module: string
  /** NON_NULL — 정책이 없으면 키가 빠진다. 있으면 `{ policies: [...] }` 로 이중 래핑이다. */
  policies?: { policies: WarehousePolicy[] } | null
}

export type WarehouseListResponse = { items: WarehouseView[] }

/** `get-warehouse-master-list` 한 항목 — 비활성 창고도 포함한다. */
export type WarehouseAdminItem = {
  warehouseCode: string
  warehouseName: string | null
  active: boolean
  attributesComplete: boolean
  missingAttributes: string[]
  lesOperating: boolean | null
}

export type WarehouseAdminList = { operationDate: string; lesLookup: 'OK' | 'FAILED'; warehouses: WarehouseAdminItem[] }

export type ChangeWarehouseStatusRequest = { warehouseCode: string; active: boolean }
export type ChangeWarehouseStatusResponse = { warehouseCode: string; active: boolean; changed: boolean }

/** 창고 목록 화면 모델. admin API 가 없으면(404) 활성 목록으로 폴백해 `mode: 'active'` 가 되고 속성 필드는 null 이다. */
export type WarehouseDirectoryItem = {
  warehouseCode: string
  warehouseName: string | null
  active: boolean
  attributesComplete: boolean | null
  missingAttributes: string[] | null
  lesOperating: boolean | null
}

export type WarehouseDirectory = { mode: 'admin' | 'active'; lesLookup: 'OK' | 'FAILED' | null; items: WarehouseDirectoryItem[] }

/** 선택 창고 정책의 출처. `none` 이면 정책을 읽을 수 없어 화면이 잠금 안내를 그린다. */
export type WarehousePolicySource = { source: 'warehouse' | 'activeList'; view: WarehouseView } | { source: 'none'; view: null }

export type UpsertWarehousePolicyRequest = { warehouseCode: string; policy: WarehousePolicy }
